import http from './httpService'
import isr from './isrService'
import nanoBananaService from './nanoBananaService'

export function createGreetCard(regKeys, storeId, zip = false, merge = false, messages = [], filename = '') {
  if (!regKeys?.length && !messages.length) return console.error('Registration keys and messages are undefined or empty')

  const config = zip ? { responseType: 'arraybuffer' } : {}

  return http.post(`/automation/greeting-card`, { regKeys, storeId, zip, merge, messages, filename }, config)
}

export function createStarChart(regKeys, storeId, zip = false, merge = false) {
  if (!regKeys?.length) return console.error('Registration keys are undefined or empty')

  const config = zip ? { responseType: 'arraybuffer' } : {}

  return http.post(`/automation/star-chart`, { regKeys, storeId, zip, merge }, config)
}

export function createStarCard(regKeys, storeId, zip = false, merge = false) {
  if (!regKeys?.length) return console.error('Registration keys are undefined or empty')

  const config = zip ? { responseType: 'arraybuffer' } : {}

  return http.post(`/automation/star-card`, { regKeys, storeId, zip, merge }, config)
}

export function createStarInfoCard(regKeys, storeId, { template, responseType } = {}) {
  if (!regKeys?.length) return console.error('Registration keys are undefined or empty')

  return http.post(`/automation/star-info-card`, { regKeys, storeId, template, responseType })
}

export function createZodiacNecklace(regKeys, storeId, props, zip = false, merge = false) {
  if (!regKeys?.length) return console.error('Registration keys are undefined or empty')

  const config = zip ? { responseType: 'arraybuffer' } : {}

  return http.post(`/automation/zodiac-necklace`, { regKeys, storeId, zip, merge, props }, config)
}

export function createCert(options) {
  const { orderIds, color, version, store, responseType } = options
  if (!orderIds) return console.log('OrderId is undefined')

  return http.post(`/certs/new`, { orderIds, color, version, store, responseType })
}

export function createShootingStarCert(options) {
  const { orderIds, store, version, responseType } = options
  if (!orderIds) return console.log('OrderIds is undefined')

  return http.post(`/certs/shooting-stars`, { orderIds, store, version, responseType })
}

export function createCertAmz(options) {
  const { registries, color, id, version, store, responseType } = options
  if (!registries) return console.log('OrderId is undefined')

  return http.post(`/certs/amazon`, { registries, color, id, version, store, responseType })
}

export function createHoroscopeAssets(orderId, version = 'digital', store) {
  if (!orderId) return console.log('OrderId is undefined')
  if (!store) return console.log('store is undefined')

  return http.get(`/horoscope/assets/${orderId}?version=${version}&store=${encodeURIComponent(store)}`)
}

/*
 * Manual CPI flow (HoroscopeBookDialog). One book per horoscope line item; all
 * routes take `?store=` and answer with the book view:
 *   { bookNumber, lineItemId, title, quantity, variant, properties,
 *     stage, content, assemblies: { print, digital }, submission, delivery, lastError }
 *   GET  /horoscope/books/:orderId                        → { orderNumber, designs, books: [view] }
 *   PATCH /horoscope/books/:orderId/:lineItemId { overrides } → view (overrides, original, editedBy)
 *   POST /horoscope/books/:orderId/:lineItemId/content    multipart `content` → view
 *   POST /horoscope/books/:orderId/:lineItemId/assemble   → view   (&version=print|digital; a digital
 *                                                            assembly cannot be submitted to CPI → 409)
 *   POST /horoscope/books/:orderId/:lineItemId/submit     → view + status ('submitted' | 'exists')
 *                                                           422 → { message, problems: string[] }
 *                                                           409 → wrong stage for this step
 *   POST /horoscope/books/:orderId/:lineItemId/deliver    { to } → view with delivery { to, at }
 *                                                           (planned: emails the digital book; 404 until built)
 */
const bookUrl = (orderId, lineItemId, action, storeId) =>
  `/horoscope/books/${orderId}/${lineItemId}/${action}?store=${encodeURIComponent(storeId)}`

export function getHoroscopeBooks(orderId, storeId) {
  return http.get(`/horoscope/books/${orderId}?store=${encodeURIComponent(storeId)}`)
}

/** Staff corrections (name, birth data, place, greeting, partner fields, design); null removes one. */
export function updateHoroscopeBook(orderId, lineItemId, storeId, overrides) {
  return http.patch(`/horoscope/books/${orderId}/${lineItemId}?store=${encodeURIComponent(storeId)}`, {
    overrides,
  })
}

export function uploadHoroscopeContent(orderId, lineItemId, file, storeId) {
  const form = new FormData()
  form.append('content', file, file.name)
  return http.post(bookUrl(orderId, lineItemId, 'content', storeId), form)
}

export function assembleHoroscopeBook(orderId, lineItemId, storeId, version = 'print') {
  return http.post(`${bookUrl(orderId, lineItemId, 'assemble', storeId)}&version=${version}`)
}

/** `resubmit`: another CPI order for a book already submitted (without it the API answers `exists`). */
export function submitHoroscopeBook(orderId, lineItemId, storeId, { resubmit = false } = {}) {
  return http.post(bookUrl(orderId, lineItemId, 'submit', storeId), resubmit ? { resubmit: true } : {})
}

export function sendHoroscopeBook(orderId, lineItemId, storeId, { to }) {
  return http.post(bookUrl(orderId, lineItemId, 'deliver', storeId), { to })
}

/*
 * Generated text (gravur). Every view carries `generation`
 * ({ status, jobId, warnings: ['kind@sectionId'], error, requests, checkedAt, approvedBy, … } or null).
 *   POST …/:lineItemId/generate                 → view + status ('requested' | 'exists' | …)
 *                                                 422 → { message, ...view } (unsupported / invalid book), 502 gravur down
 *   POST …/:lineItemId/generation/approve       → view (releases a needs_review report; 409 when none waits)
 *   POST …/:lineItemId/generation/sections/:sectionId/regenerate → 202 view; gravur's 404 / 409 pass through
 */
/** `regenerate`: a new text for the whole book although one exists (staff confirmed the cost). */
export function generateHoroscopeContent(orderId, lineItemId, storeId, { regenerate = false } = {}) {
  return http.post(bookUrl(orderId, lineItemId, 'generate', storeId), regenerate ? { regenerate: true } : undefined)
}

/** Fit the generated chapters to their pages again (rest-api measures and has gravur rewrite short ones). */
export function fitHoroscopePages(orderId, lineItemId, storeId) {
  return http.post(bookUrl(orderId, lineItemId, 'generation/fit', storeId))
}

/** Stop the book's running generation (pending, queued or processing); `status: finished` when it was too late. */
export function cancelHoroscopeGeneration(orderId, lineItemId, storeId) {
  return http.post(bookUrl(orderId, lineItemId, 'generation/cancel', storeId))
}

export function approveHoroscopeContent(orderId, lineItemId, storeId) {
  return http.post(bookUrl(orderId, lineItemId, 'generation/approve', storeId))
}

export function regenerateHoroscopeSection(orderId, lineItemId, sectionId, storeId) {
  return http.post(
    bookUrl(orderId, lineItemId, `generation/sections/${encodeURIComponent(sectionId)}/regenerate`, storeId)
  )
}

/*
 * Manual books (ManageMizodiaCustom): a book without a Shopify order. The
 * created order's id is `manual:<orderNumber>`, which every route above accepts.
 *   POST /horoscope/books/manual { orderNumber, variant, delivery, properties, customer?, shippingAddress? }
 *        → { orderId, orderNumber, designs, books: [view] }   409 when the book number is taken
 *   GET  /horoscope/books/manual → { orders: [{ orderId, orderNumber, customer, shippingAddress, createdAt, books }] }
 *   POST /horoscope/books/manual/:orderId/books { variant, bookType?, package, properties }
 *        → the order with all its books + bookNumber (the new one: <nr>-2, <nr>-3, …);
 *        customer and shipping address are the order's (422 for print when it has none)
 *   PATCH /horoscope/books/manual/:orderId { orderNumber } → the order as GET lists it, under its new id
 *        409 when the number is taken or its text is being generated; a book ordered at CPI keeps its submission
 */
export function createManualHoroscopeBook(storeId, body) {
  return http.post(`/horoscope/books/manual?store=${encodeURIComponent(storeId)}`, body)
}

export function addManualHoroscopeBook(storeId, orderId, body) {
  return http.post(
    `/horoscope/books/manual/${encodeURIComponent(orderId)}/books?store=${encodeURIComponent(storeId)}`,
    body
  )
}

export function renameManualHoroscopeOrder(storeId, orderId, orderNumber) {
  return http.patch(
    `/horoscope/books/manual/${encodeURIComponent(orderId)}?store=${encodeURIComponent(storeId)}`,
    { orderNumber }
  )
}

export function getManualHoroscopeOrders(storeId, limit = 50) {
  return http.get(`/horoscope/books/manual?store=${encodeURIComponent(storeId)}&limit=${limit}`)
}

export function createCustomCert(data, color, type, lang) {
  return http.post(`/certs/custom`, { data, color, type, lang })
}

export function createSkymapProduct(apiPath, template, data, filename) {
  console.log({ template, data, filename })
  return isr.post(apiPath, { template, data, filename })
}

export function createTextSvg(data, filename) {
  return isr.post('/svg/generate', { template: 'svg/text', data, filename })
}

export function createFlowerBouquetSvg(data, filename) {
  return isr.post('/svg/generate', { template: 'svg/flower-bouquet', data, filename })
}

export function generateSvg(body) {
  return http.post('/svgs/generate', body)
}

export function generateEdits(model, prompt, file, fileType, orderNumber) {
  return nanoBananaService.post('image-edits/generate', {
    model,
    prompt,
    file,
    fileType,
    orderNumber,
  })
}

export function generateEditsAndProcess(model, prompt, file, fileType, orderNumber) {
  return nanoBananaService.post('image-edits/generate-and-process', {
    model,
    prompt,
    file,
    fileType,
    orderNumber,
  })
}

export function fetchFilteredImages(orderNumber) {
  return nanoBananaService.post('images/filter', { orderNumber })
}

/** Delete a generated edit file on nanoBanana storage (by server filename). */
export function deleteNanoBananaImage(filename) {
  return nanoBananaService.delete('images', { data: { filename } })
}

export function createCertBulk(orderIds, version, store, responseType) {
  if (!orderIds) return console.log('orderId is undefined')

  // const config = responseType === 'zip' ? { responseType: 'arraybuffer' } : {}

  return http.post(
    `/certs/new`,
    {
      orderIds,
      version,
      store,
      responseType,
    }
    //config
  )
}

